import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Eyebrow, Pill } from '../../../ds/components';
import { clsx } from '../../../ds/clsx';
import type { ContactFinancing } from '../types';
import { calculationHref, financingBandView, matchingUnitsHref } from './financing-band';
import styles from './FinancingBand.module.css';

export interface FinancingBandProps {
  contactId: string;
  financing: ContactFinancing;
  /** „Jetzt berechnen“ / „Neu berechnen“; resolves when the record is refreshed. */
  onCalculate: () => Promise<void>;
}

/**
 * Financing capacity directly under the record head (D-380): dark band with the hero figure once calculated,
 * warning band while the returned Selbstauskunft is not calculated, muted band when the calculation expired,
 * a thin line while the link is out. Nothing without a Selbstauskunft (and for roles that never see one).
 */
export function FinancingBand({ contactId, financing, onCalculate }: FinancingBandProps) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const view = financingBandView(financing);
  if (view === null) return null;

  async function calculate() {
    setBusy(true);
    try {
      await onCalculate();
    } finally {
      setBusy(false);
    }
  }

  if (view.tone === 'sent') {
    return (
      <div className={styles.sent} role="status">
        {view.title}
      </div>
    );
  }

  if (view.tone === 'calculated') {
    const calcHref = calculationHref(contactId, financing);
    const unitsHref = matchingUnitsHref(financing);
    return (
      <section className={clsx(styles.band, styles.calculated)} aria-label="Finanzierbarkeit">
        <div className={styles.body}>
          <div className={styles.eyebrowRow}>
            <Eyebrow as="div" className={styles.darkEyebrow}>
              Finanzierbarkeit
            </Eyebrow>
            <Pill variant="success" dot>
              Finanzierbar
            </Pill>
          </div>
          <div className={styles.hero}>{view.title}</div>
          {view.line ? <div className={styles.darkLine}>{view.line}</div> : null}
        </div>
        <div className={styles.actions}>
          {calcHref ? (
            <Button variant="primary" onClick={() => void navigate(calcHref)}>
              Zur Berechnung
            </Button>
          ) : null}
          {unitsHref ? (
            <Button variant="ghostInverse" onClick={() => void navigate(unitsHref)}>
              Passende Einheiten
            </Button>
          ) : null}
        </div>
      </section>
    );
  }

  const returned = view.tone === 'returned';
  return (
    <section
      className={clsx(styles.band, returned ? styles.returned : styles.expired)}
      aria-label="Finanzierbarkeit"
      role={returned ? 'alert' : 'status'}
    >
      <div className={styles.body}>
        <div className={styles.title}>{view.title}</div>
        {view.line ? <div className={styles.line}>{view.line}</div> : null}
      </div>
      <div className={styles.actions}>
        <Button variant={returned ? 'primary' : 'secondary'} loading={busy} onClick={() => void calculate()}>
          {returned ? 'Jetzt berechnen' : 'Neu berechnen'}
        </Button>
      </div>
    </section>
  );
}
